import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import cx from "classnames";

import styles from "./card.module.scss";
import Thumbnail from "src/components/thumbnail";
import Text from "src/components/text";
import { SimulationSummary } from "src/types/coaching/simulation";
import { CALL_TYPES } from "src/constants/simulation";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { Podcast } from "src/types/coaching/podcast";
import { FileService } from "src/utils/file.service";


const defaultCourse = "/images/assets/courseThumbnail.jpg";
const defaultPodcast = "/images/assets/podcastThumbnail.jpg"
const defaultSimulation = "/images/assets/simulationThumbnail.jpg"
interface SimulationCardProps {
  simulation: SimulationSummary;
  url: string;
  status: "draft" | "published";
}

const SimulationCard: React.FC<SimulationCardProps> = ({ simulation, url, status }) => {
  const location = useLocation();
  const callType = useMemo(() => {
    const value = _.get(simulation, "callType") || "";
    return CALL_TYPES.find((item) => item.id === value)?.title || "";
  }, [simulation]);

  const duration = useMemo(() => _.get(simulation, 'duration') || 0, [simulation]);

  const timeBound = useMemo(() => {
    return _.get(simulation, "timeBound");
  }, [simulation]);

  const userThumbnail = useSelector((state: any) => {
    return _.get(state, 'users.me.icon');
  });

  return (
    <div className={cx(styles.card, styles.simulation, styles[status])}>
      <div className={styles.header}>
        <div className={styles.avatar}>
          <Thumbnail media={simulation.persona?.icon} alt="Persona" className={cx(styles.persona,{
                [styles.discovery]: callType === "Discovery Call",
                [styles.coldcall]: callType === "Cold Call",
                [styles.elevatorPitch]: callType === "Elevator Pitch"
              })} width={200} defaultImage={defaultSimulation} />
        </div>
        <div className={styles.courseType}>
          <i className="paperflite-phone-call" />
        </div>
        {status !== "published" &&
          <div className={styles.status}>Draft</div>
        }
      </div>
      <div className={styles.metadataRow}>
        <div className={styles.metadatas}>
          {callType === "Elevator Pitch" && (
            <div className={styles.metadata}>
              <i className="paperflite-clock"></i>
              <span className={styles.label}>{FileService.formatTimeBound(timeBound ?? 0)}</span>
            </div>
          )}
          {callType !== "Elevator Pitch" && (
            <div className={styles.metadata}>
              <i className="paperflite-clock"></i>
              <span className={styles.label}>{duration} Minutes</span>
            </div>
          )}
          <div className={styles.separator} />
          <div className={styles.metadata}>
            <i className="paperflite-rocket"></i>
            <span className={styles.label}>{callType}</span>
          </div>
        </div>
        <div className={styles.actions}>
          <Link to={url} state={{ from: location.pathname }} className={cx(styles.action, styles.start)}>
            <i className="paperflite-right-arrow" />
            <span>open simulation</span>
          </Link>
        </div>
      </div>
      <div className={styles.body}>
        <Text className={styles.name} text={simulation.name || ""} lines={1} />
        <Text className={styles.description} tooltip={false} text={simulation.description || ""} lines={3} />
      </div>
    </div>
  );
}

const PlaceholderSimulationCard: React.FC<object> = () => {
  return (
    <div className={cx(styles.card, styles.simulation, styles.placeholder)}>
      <div className={styles.header}>
        <div className={styles.avatar}>
          <div className={cx(styles.persona, styles.block)} />
          <div className={cx(styles.user, styles.block)} />
        </div>
        <div className={styles.courseType}>
          <i className="paperflite-phone-call" />
        </div>
        <div className={styles.status}>Draft</div>
      </div>
      <div className={styles.metadataRow}>
        <div className={styles.metadatas}>
          <div className={styles.metadata}>
            <i className="paperflite-clock"></i>
            <span className={cx(styles.label, styles.block)}></span>
          </div>
          <div className={styles.separator} />
          <div className={styles.metadata}>
            <i className="paperflite-rocket"></i>
            <span className={cx(styles.label, styles.block)}></span>
          </div>
        </div>
      </div>
      <div className={styles.body}>
        <div className={cx(styles.name, styles.block)} />
        <div className={cx(styles.description)}>
          <div className={cx(styles.block, styles.line)} />
          <div className={cx(styles.block, styles.line)} />
          <div className={cx(styles.block, styles.line)} />
        </div>
      </div>
    </div>
  );
}

interface FeaturedSimulationCardProps {
  simulation: SimulationSummary;
  url: string;
}

const FeaturedSimulationCard: React.FC<FeaturedSimulationCardProps> = ({ simulation, url }) => {

  const location = useLocation();

  const callType = useMemo(() => {
    const value = _.get(simulation, "callType") || "";
    return CALL_TYPES.find((item) => item.id === value)?.title || "";
  }, [simulation]);

  const duration = useMemo(() => _.get(simulation, 'duration') || 0, [simulation]);

  const timeBound = useMemo(() => {
    return _.get(simulation, "timeBound");
  }, [simulation]);
  
  const banner = useMemo(() => _.get(simulation, 'banner.background'), [simulation]);

  return (
    <div className={cx(styles.featuredCard, styles.simulation)}>
      <div className={styles.metadatas}>
        <div className={cx(styles.metadata, styles.type)}>
          <i className="paperflite-phone-call" />
          <span className={styles.label}>Simulation</span>
        </div>
        {callType === "Elevator Pitch" && (
          <div className={styles.metadata}>
            <i className="paperflite-clock"></i>
            <span className={styles.label}>{FileService.formatTimeBound(timeBound ?? 0)}</span>
          </div>
        )}
        {callType !== "Elevator Pitch" && (
          <div className={styles.metadata}>
            <i className="paperflite-clock"></i>
            <span className={styles.label}>{duration} Minutes</span>
          </div>
        )}
        <div className={styles.metadata}>
          <i className="paperflite-rocket"></i>
          <span className={styles.label}>{callType}</span>
        </div>
      </div>
      <div className={styles.body}>
        <Thumbnail
          media={banner}
          alt="Simulation"
          className={cx(styles.thumbnail,{
            [styles.discovery]: callType === "Discovery Call",
            [styles.coldcall]: callType === "Cold Call",
            [styles.elevatorPitch]: callType === "Elevator Pitch"
          })}
          width={200}
          defaultImage={defaultSimulation}
        />
        <div className={styles.details}>
          <Link to={url} state={{ from: location.pathname }}className={cx(styles.name)}>
            <Text text={simulation.name || ""} lines={1} />
          </Link>
          <Text className={styles.description} tooltip={false} text={simulation.description || ""} lines={3} />
        </div>
      </div>
    </div>
  );
}

const PlaceholderFeaturedSimulationCard: React.FC<object> = () => {
  return (
    <div className={cx(styles.featuredCard, styles.simulation, styles.placeholder)}>
      <div className={styles.metadatas}>
        <div className={styles.metadata}>
          <i className="paperflite-phone-call" />
          <span className={cx(styles.label, styles.block)}></span>
        </div>
        <div className={styles.metadata}>
          <i className="paperflite-clock"></i>
          <span className={cx(styles.label, styles.block)}></span>
        </div>
        <div className={styles.metadata}>
          <i className="paperflite-rocket"></i>
          <span className={cx(styles.label, styles.block)}></span>
        </div>
      </div>
      <div className={styles.body}>
        <div className={cx(styles.thumbnail, styles.block)} />
        <div className={styles.details}>
          <div className={cx(styles.name, styles.block)} />
          <div className={cx(styles.description)}>
            <div className={cx(styles.block, styles.line)} />
            <div className={cx(styles.block, styles.line)} />
            <div className={cx(styles.block, styles.line)} />
          </div>
        </div>
      </div>
    </div>
  );
}

const PodcastCard: React.FC<PodcastCardProps> = ({ podcast, url, status }) => {

  const location = useLocation();

  const duration = useMemo(() => _.get(podcast, 'duration') || 0, [podcast]);
  const thumbnail = useMemo(() => _.get(podcast, 'banner.background'), [podcast]);
  const position = useMemo(() => _.get(podcast, 'banner.position'), [podcast]);
  const crop = useMemo(() => _.get(podcast, 'banner.crop'), [podcast]);

  return (
    <div className={cx(styles.card, styles.podcast, styles[status])}>
      <div className={styles.header}>
        <div className={styles.thumbnail}>
          <Thumbnail media={thumbnail} alt="Podcast" className={styles.thumbnailImage} width={200} defaultImage={defaultPodcast} position={position} crop={crop} />
        </div>
        <div className={styles.courseType}>
          <i className="paperflite-mic" />
        </div>
        {status !== "published" &&
          <div className={styles.status}>Draft</div>
        }
      </div>
      <div className={styles.metadataRow}>
        <div className={styles.metadatas}>
          {duration > 0 &&
            <>
              <div className={styles.metadata}>
                <i className="paperflite-clock"></i>
                <span className={styles.label}>{FileService.formatDuration(duration, 'long')}</span>
              </div>
              <div className={styles.separator} />
            </>
          }
          <div className={styles.metadata}>
            <i className="paperflite-mic"></i>
            <span className={styles.label}>podcast</span>
          </div>
        </div>
        <div className={styles.actions}>
          <Link to={url} state={{ from: location.pathname }}className={cx(styles.action, styles.start)}>
            <i className="paperflite-right-arrow" />
            <span>open podcast</span>
          </Link>
        </div>
      </div>
      <div className={styles.body}>
        <Text className={styles.name} text={podcast.name || ""} lines={1} />
        <Text className={styles.description} tooltip={false} text={podcast.description || ""} lines={3} />
      </div>
    </div>
  );
}

interface PodcastCardProps {
  podcast: Podcast;
  url: string;
  status: "draft" | "published";
}

const PlcaholderPodcastCard: React.FC<object> = () => {
  return (
    <div className={cx(styles.card, styles.podcast, styles.placeholder)}>
      <div className={styles.header}>
        <div className={styles.avatar}>
          <div className={cx(styles.persona, styles.block)} />
          <div className={cx(styles.user, styles.block)} />
        </div>
        <div className={styles.courseType}>
          <i className="paperflite-phone-call" />
        </div>
        <div className={styles.status}>Draft</div>
      </div>
      <div className={styles.metadataRow}>
        <div className={styles.metadatas}>
          <div className={styles.metadata}>
            <i className="paperflite-clock"></i>
            <span className={cx(styles.label, styles.block)}></span>
          </div>
          <div className={styles.separator} />
          <div className={styles.metadata}>
            <i className="paperflite-rocket"></i>
            <span className={cx(styles.label, styles.block)}></span>
          </div>
        </div>
      </div>
      <div className={styles.body}>
        <div className={cx(styles.name, styles.block)} />
        <div className={cx(styles.description)}>
          <div className={cx(styles.block, styles.line)} />
          <div className={cx(styles.block, styles.line)} />
          <div className={cx(styles.block, styles.line)} />
        </div>
      </div>
    </div>
  );
}

interface FeaturedPodcastCardProps {
  podcast: Podcast;
  url: string;
}

const FeaturedPodcastCard: React.FC<FeaturedPodcastCardProps> = ({ podcast, url }) => {
  const location = useLocation();

  const duration = useMemo(() => _.get(podcast, 'duration') || 0, [podcast]);
  const thumbnail = useMemo(() => _.get(podcast, 'banner.background'), [podcast]);
  const position = useMemo(() => _.get(podcast, 'banner.position'), [podcast]);
  const crop = useMemo(() => _.get(podcast, 'banner.crop'), [podcast]);

  return (
    <div className={cx(styles.featuredCard, styles.simulation)}>
      <div className={styles.metadatas}>
        <div className={cx(styles.metadata, styles.type)}>
          <i className="paperflite-mic" />
          <span className={styles.label}>Podcast</span>
        </div>
        {duration > 0 &&
          <>
            <div className={styles.metadata}>
              <i className="paperflite-clock"></i>
              <span className={styles.label}>{FileService.formatDuration(duration, 'long')}</span>
            </div>
          </>
        }
        <div className={styles.metadata}>
          <span className={styles.label}>Type: Conversational</span>
        </div>
      </div>
      <div className={styles.body}>
        <Thumbnail
          media={thumbnail}
          alt="Podcast"
          className={styles.thumbnail}
          width={200}
          defaultImage={defaultPodcast}
          position={position}
          crop={crop}
        />
        <div className={styles.details}>
          <Link to={url} state={{ from: location.pathname }} className={cx(styles.name)}>
            <Text text={podcast.name || ""} lines={1} />
          </Link>
          <Text className={styles.description} tooltip={false} text={podcast.description || ""} lines={3} />
        </div>
      </div>
    </div>
  );
}


export const Simulation = {
  Card: SimulationCard,
  PlaceholderCard: PlaceholderSimulationCard,
  Featured: FeaturedSimulationCard,
  PlaceholderFeatured: PlaceholderFeaturedSimulationCard,
}

export const Podcasts = {
  Card: PodcastCard,
  PlaceholderCard: PlcaholderPodcastCard,
  Featured: FeaturedPodcastCard,
}